import React, { useCallback, useEffect, useId, useRef } from 'react';
import { CloseOutlined } from '@ant-design/icons';

import 'containers/Intelligence/theme/fonts';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { useOrbitContext } from '../OrbitContext';
import { useOrbitNotice } from '../OrbitNoticeContext';
import { useOrbitThemeVariant } from '../OrbitThemeScope';
import { TOrbit } from '../i18n/TModules';
import OrbitHistoryIcon from '../icons/OrbitHistoryIcon';
import OrbitNewChatIcon from '../icons/OrbitNewChatIcon';
import {
  OrbitBody,
  OrbitBodyArea,
  OrbitBodyScrim,
  OrbitBodyUnderlay,
  OrbitFooter,
  OrbitHeader,
  OrbitHeaderActionButton,
  OrbitHeaderActions,
  OrbitHeaderGroup,
  OrbitHeaderIcon,
  OrbitHeaderTipBox,
  OrbitPanel,
  OrbitSessionBadge,
  OrbitTitle,
  OrbitTitleBlock,
  orbitColorsFor
} from '../styles';
import { ORBIT_SCROLL_CONTAINER_ATTR } from '../threadScroll';
import OrbitChatNameBar from './OrbitChatNameBar';
import { ORBIT_SIDEBAR_ID } from './OrbitLauncher';
import OrbitMark from './OrbitMark';

export interface IOrbitHeaderTipProps {
  children: React.ReactNode;
  /**
   * Handle for the wrapper, because the bubble is a pseudo-element and cannot carry one of its own.
   */
  testId: string;
  title: string;
}

/**
 * Hover and focus label for a header glyph. The wrapper publishes the text as `data-tooltip` and
 * `OrbitHeaderTipBox` draws it; there is no antd `Tooltip` and so no `rc-align`, which is the one
 * thing on this hover path that opened native ResizeObservers (PLA-8844,
 * `browser-recheck22-20260922.md`).
 *
 * The action cluster sits at the panel's right edge and the panel itself is flush with the right
 * edge of the viewport, so the bubble is anchored by its RIGHT edge and grows inwards: a centred
 * or left-anchored one hangs off the screen, measured at both 1440x900 and 1280x720 (user,
 * 2026-09-14).
 */
export const OrbitHeaderTip: React.FC<IOrbitHeaderTipProps> = ({ children, testId, title }) => (
  <OrbitHeaderTipBox data-testid={testId} data-tooltip={title}>
    {children}
  </OrbitHeaderTipBox>
);

export interface IOrbitSidebarProps {
  /** Panel body; later units fill it with the home state, thread and conversation list. */
  children?: React.ReactNode;
  /** Panel footer (composer + disclaimer); rendered only when provided. */
  footer?: React.ReactNode;
  /**
   * Whether the conversation list is the body on screen. The header's History glyph reads as
   * pressed while it is, and clicking it again closes the list.
   */
  historyOpen?: boolean;
  /**
   * Renders the New Chat and History glyphs inert. The shell raises it while a decision about the
   * running investigation is on the panel's scrim, so the header cannot change screens underneath
   * it.
   */
  newChatDisabled?: boolean;
  /**
   * Starts a fresh conversation from the header's New Chat glyph. The shell supplies it so the
   * conversation list closes and the panel returns to its first screen with the session it was
   * showing; without it the panel's own `newChat` runs, which is all a sidebar rendered on its own
   * needs.
   */
  onNewChat?: () => void;
  /**
   * Toggles the conversation list from the header's History glyph (`5118:16678`). The shell owns
   * which body is drawn, so it supplies the same `toggleConversations` the idle and home panels'
   * "View all history" link uses; without it the glyph has nothing to open and is not drawn.
   */
  onHistory?: () => void;
  /**
   * An interrupting layer drawn over the body and the composer on the panel's own scrim. While it
   * is present everything under it is marked `inert`, so the scrimmed transcript and the composer
   * cannot be reached by Tab or by a screen reader. The page beside the panel is untouched: the
   * panel stays non-modal to it, and Tab still leaves the panel.
   */
  overlay?: React.ReactNode;
}

const OrbitSidebar: React.FC<IOrbitSidebarProps> = ({
  children,
  footer,
  historyOpen = false,
  newChatDisabled = false,
  onNewChat,
  onHistory,
  overlay
}) => {
  const {
    open,
    closeSidebar,
    newChat,
    restoreOpenerFocus,
    sessionId,
    turns,
    activeTurnId,
    pendingQuestion,
    turnsLoading
  } = useOrbitContext();
  const { t } = useMultiLanguageTranslation();
  const { setAnchor } = useOrbitNotice();
  const titleId = useId();
  const panelRef = useRef<HTMLElement | null>(null);
  const underlayRef = useRef<HTMLDivElement>(null);
  const overlaid = overlay !== undefined && overlay !== null && overlay !== false;
  const themeVariant = useOrbitThemeVariant();
  const markFill = orbitColorsFor(themeVariant).mark;

  /**
   * The panel publishes its own node so `OrbitNoticeHost` - mounted a level above it, by the
   * provider - can portal the notice bubble INSIDE the panel and position it against the panel's
   * box. Closing the panel returns null here, which clears the anchor and takes the bubble with it.
   */
  const setPanelNode = useCallback(
    (node: HTMLElement | null) => {
      panelRef.current = node;
      setAnchor(node);
    },
    [setAnchor]
  );

  // Set on the node rather than passed as JSX: React 18 has no `inert` prop and would drop it.
  useEffect(() => {
    const underlay = underlayRef.current;

    if (underlay === null || !overlaid) {
      return undefined;
    }

    underlay.setAttribute('inert', '');
    underlay.setAttribute('aria-hidden', 'true');

    return () => {
      underlay.removeAttribute('inert');
      underlay.removeAttribute('aria-hidden');
    };
    // `open` is a dependency because the underlay does not exist while the panel is closed: a card
    // already pending when the user opens the panel would otherwise never reach this effect.
  }, [overlaid, open]);

  // Focus starts on the panel and returns to whatever opened it, so a keyboard user who pressed the
  // launcher is neither left on a removed button nor dropped at the top of the page. The opener is
  // captured by the provider at toggle time, not read here: by the time this effect runs the
  // launcher has already unmounted and `document.activeElement` is `<body>`.
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    panelRef.current?.focus({ preventScroll: true });

    return restoreOpenerFocus;
  }, [open, restoreOpenerFocus]);

  if (!open) {
    return null;
  }

  // Escape closes; Tab is left to the browser. The panel is a non-modal `complementary` region, so
  // wrapping Tab inside it would be a keyboard trap (WCAG 2.1.2): the user must be able to Tab back
  // out to the page the panel sits beside.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      closeSidebar();
    }
  };

  const closeLabel = t(`${TOrbit}.actions.close`);
  const newChatLabel = t(`${TOrbit}.actions.newChat`);
  const historyLabel = t(`${TOrbit}.actions.history`);
  const startNewChat = onNewChat ?? newChat;
  // The body is the thread whenever the shell is not drawing its list or an empty first screen: a
  // conversation is on record, being asked or loading.
  const chatOpen =
    !historyOpen &&
    (turns.length > 0 || activeTurnId !== null || pendingQuestion !== null || turnsLoading);
  const focusPanel = () => panelRef.current?.focus({ preventScroll: true });

  return (
    <OrbitPanel
      ref={setPanelNode}
      id={ORBIT_SIDEBAR_ID}
      role="complementary"
      aria-labelledby={titleId}
      tabIndex={-1}
      data-testid="orbit-sidebar"
      onKeyDown={handleKeyDown}
    >
      <OrbitHeader>
        <OrbitHeaderGroup>
          {/* 5017:9836 / 5118:16828, the designer's export: five vectors at 24 x 25. */}
          <OrbitHeaderIcon aria-hidden="true">
            <OrbitMark width={24} height={25} fill={markFill} />
          </OrbitHeaderIcon>
          <OrbitTitleBlock>
            <OrbitTitle id={titleId} data-testid="orbit-sidebar-title">
              {t(`${TOrbit}.title`)}
            </OrbitTitle>
            {sessionId !== null ? (
              <OrbitSessionBadge data-testid="orbit-sidebar-session-badge">
                {t(`${TOrbit}.notice.activeSession`)}
              </OrbitSessionBadge>
            ) : null}
          </OrbitTitleBlock>
        </OrbitHeaderGroup>
        {/*
          6034:5865 as re-read at 12:45, one row: the `chat4` glyph (6034:5885), the chat-check
          (6712:6592) and the close glyph. The design was still being edited during the read, so
          the set is provisional. It drops the minimise bar, which was never built (minimise and
          close both collapse the panel to the launcher), and the disabled `save-01`, which had no
          endpoint behind it.

          New Chat is the affordance the composer lost when its leading "+" was dropped
          (2026-09-14); `newChat` forgets the session locally and sends nothing until the next
          question, so the glyph needs no contract. It also replaced the header's Back arrow
          (user, 2026-09-14): one click both forgets the session and returns the panel to its
          first screen, which is why it is wired on that screen too.

          The tip box around each glyph is also the hover hook for the disabled states
          (conventions/components/cross-browser.md section 2).
        */}
        <OrbitHeaderActions data-testid="orbit-sidebar-actions">
          <OrbitHeaderTip testId="orbit-sidebar-new-chat-tip" title={newChatLabel}>
            <OrbitHeaderActionButton
              type="button"
              disabled={newChatDisabled}
              style={newChatDisabled ? { pointerEvents: 'none' } : undefined}
              $w={20}
              $h={20}
              aria-label={newChatLabel}
              data-testid="orbit-sidebar-new-chat"
              onClick={startNewChat}
            >
              <OrbitNewChatIcon />
            </OrbitHeaderActionButton>
          </OrbitHeaderTip>
          {/*
            The chat-check glyph is the History control (user, 2026-09-15): it opens the
            conversation list the panel's own "View all history" link opens, and reads as pressed
            while that list is the body. It is inert under the conflict card for the same reason
            New Chat is - the decision is about the running investigation, and the list is a
            different body altogether.
          */}
          {onHistory !== undefined ? (
            <OrbitHeaderTip testId="orbit-sidebar-history-tip" title={historyLabel}>
              <OrbitHeaderActionButton
                type="button"
                disabled={newChatDisabled}
                style={newChatDisabled ? { pointerEvents: 'none' } : undefined}
                $w={17}
                $h={17}
                aria-label={historyLabel}
                aria-pressed={historyOpen}
                data-testid="orbit-sidebar-history"
                onClick={onHistory}
              >
                <OrbitHistoryIcon />
              </OrbitHeaderActionButton>
            </OrbitHeaderTip>
          ) : null}
          <OrbitHeaderTip testId="orbit-sidebar-close-tip" title={closeLabel}>
            <OrbitHeaderActionButton
              type="button"
              $w={14}
              $h={20}
              aria-label={closeLabel}
              data-testid="orbit-sidebar-close"
              onClick={closeSidebar}
            >
              <CloseOutlined aria-hidden="true" />
            </OrbitHeaderActionButton>
          </OrbitHeaderTip>
        </OrbitHeaderActions>
      </OrbitHeader>
      <OrbitBodyArea>
        <OrbitBodyUnderlay ref={underlayRef} data-testid="orbit-sidebar-underlay">
          {/* 6654:2155 replaces "Context Detected" with a fixed bar the thread scrolls under. */}
          {chatOpen ? <OrbitChatNameBar onDeleted={focusPanel} /> : null}
          <OrbitBody
            $thread={chatOpen}
            data-testid="orbit-sidebar-body"
            {...{ [ORBIT_SCROLL_CONTAINER_ATTR]: 'true' }}
          >
            {children}
          </OrbitBody>
          {footer !== undefined ? (
            <OrbitFooter data-testid="orbit-sidebar-footer">{footer}</OrbitFooter>
          ) : null}
        </OrbitBodyUnderlay>
        {overlaid ? (
          <OrbitBodyScrim data-testid="orbit-sidebar-scrim">{overlay}</OrbitBodyScrim>
        ) : null}
      </OrbitBodyArea>
    </OrbitPanel>
  );
};

export default OrbitSidebar;
